/**
 * Every figure and date in the portal goes through here, so the same amount never
 * appears two ways on two screens.
 *
 * Mostly the staff application's `format.ts`, with the money functions replaced. The
 * staff application knows a country's currency symbol and its number of decimal places
 * because it holds the country record; the portal holds no country at all - a partner is
 * one company, and their deals may span several - so every figure arrives with nothing
 * but a three-letter code beside it.
 */

/**
 * How many decimal places a currency actually has.
 *
 * Asked of `Intl` rather than kept in a table here, because a table would be a second
 * source of truth for something the platform already knows: IDR has no minor unit, so a
 * rupiah figure carries no decimals rather than two zeros nobody can pay.
 */
export function minorUnits(currencyCode: string): number {
  try {
    // Optional in the platform's own typings, so the fallback is not theatre: an engine
    // that resolves no digit count leaves this undefined.
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).resolvedOptions().maximumFractionDigits ?? 2
  } catch {
    // An unrecognised code. Two places is the common case and reads as money; throwing
    // here would take a whole statement off the screen over a formatting question.
    return 2
  }
}

/**
 * The digits alone. The currency code is rendered beside the figure by the caller,
 * because these screens group by currency and never add across it - two totals, never
 * one, and no FX rate anywhere in this system.
 */
export function money(value: number, currencyCode: string): string {
  const places = minorUnits(currencyCode)

  return value.toLocaleString('en-US', {
    minimumFractionDigits: places,
    maximumFractionDigits: places,
  })
}

export function percent(value: number, places = 2): string {
  return `${value.toFixed(places)}%`
}

export function date(value: string | null | undefined): string {
  if (!value) return '-'

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '-'

  return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
}

export function shortDate(value: string | null | undefined): string {
  if (!value) return '-'

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '-'

  return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })
}

/**
 * Date and time together, for a record's own history.
 *
 * Local to the reader, because the question being answered is "when did this happen to
 * me", not "what did the server's clock say".
 */
export function dateTime(value: string | null | undefined): string {
  if (!value) return '-'

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '-'

  return parsed.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'

  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''

  return (first + last).toUpperCase()
}
